import _ from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { Breadcrumb } from '../../../../../common/widgets/Breadcrumbs';
import { TopLevelCategoryBasePath } from '../../../constants';
import { ProductTypes } from '../../../enums';
import { ErrorState } from '../../../models/Errors';
import { ProductOption } from '../../../models/Options';
import { PaymentMethodModel } from '../../../models/PaymentMethods';
import {
  CashBackModel,
  ContactVoucherModel,
  DefaultProductDetailsModel,
  FreeVoucherWithLinkModel,
  FreeVoucherWithPdfModel,
  PathModel,
  ProductDetailsBaseModel,
  TaxInformation,
} from '../../../models/Product';
import { ProductPaymentMethodDetailsModel } from '../../../models/ProductPaymentMethodDetailsModel';
import { ProductDetailsFormProps } from './common';
import { Budget } from '../../../contexts/common';

//#region Product props mapper

export const mapProductDetailsProps = (
  product: ProductDetailsBaseModel,
  paymentMethodDetails: ProductPaymentMethodDetailsModel[],
  paymentMethods: PaymentMethodModel[],
  errors: ErrorState,
  setErrors: Dispatch<SetStateAction<ErrorState>>,
  setPaymentError: (value: string | undefined) => void,
  customerBudget: Budget,
  updateCustomerBudgetCashback: (value: number) => void,
): ProductDetailsFormProps => {
  return {
    productId: product.productId,
    productName: product.name,
    productTypeId: product.productTypeId,
    hidePriceAndTaxes: product.hidePriceAndTaxes,
    isFavorite: product.isFavorite,
    defaultPrice: getDefaultPrice(product),
    discountedPrice: getDiscountedPrice(product),
    priceRanges: getPriceRanges(product),
    priceOptionId: getPriceOptionId(product),
    voucherDiscount: getVoucherDiscount(product),
    voucherCode: getVoucherCode(product),
    url: getLink(product),
    taxRate: getProductTaxRate(product),
    options: getProductOptions(product),
    customClientType: (product as FreeVoucherWithPdfModel).customClientType,
    dependentsConfig: (product as FreeVoucherWithPdfModel).dependentsConfig,
    recurrences: product.recurrences ?? [],
    isRecurring: product.isRecurring,
    softLimitMessage: product.softLimitMessage,
    taxInformation: getTaxInformation(product),
    isPayableByCard:
      product.productTypeId === ProductTypes.DefaultProduct &&
      (product as DefaultProductDetailsModel).isPayableByCard,
    isPayableByCashback:
      product.productTypeId === ProductTypes.DefaultProduct &&
      (product as DefaultProductDetailsModel).isPayableByCashback,
    showNoCodes:
      product.productTypeId === ProductTypes.DefaultProduct &&
      (product as DefaultProductDetailsModel).hasNoCodes,
    showTemporaryDeactivate:
      (product.productTypeId === ProductTypes.DefaultProduct &&
        (product as DefaultProductDetailsModel).temporaryDeactivate) ||
      (product.productTypeId === ProductTypes.FreePdfVoucher &&
        (product as FreeVoucherWithPdfModel).temporaryDeactivate),
    paymentMethodDetails,
    paymentMethods,
    customLabel: product.customLabel,
    canElectNow: product.electionWindowInfoModel.canElectNow,
    errors,
    setErrors,
    setPaymentError,
    customerBudget,
    updateCustomerBudgetCashback,
  };
};

const getProductOptions = (
  product: ProductDetailsBaseModel,
): ProductOption[] => {
  switch (product.productTypeId) {
    case ProductTypes.ContactVoucher:
      return (product as ContactVoucherModel).options;
    case ProductTypes.DefaultProduct:
      return (product as DefaultProductDetailsModel).options;
    case ProductTypes.FreePdfVoucher:
      return (product as FreeVoucherWithPdfModel).options;
    default:
      return [] as ProductOption[];
  }
};

const getDefaultPrice = (product: ProductDetailsBaseModel): number => {
  return product.productTypeId === ProductTypes.DefaultProduct
    ? (product as DefaultProductDetailsModel).price.defaultPrice
    : 0;
};

const getDiscountedPrice = (
  product: ProductDetailsBaseModel,
): number | undefined => {
  return product.productTypeId === ProductTypes.DefaultProduct
    ? (product as DefaultProductDetailsModel).price.discountedPrice
    : undefined;
};

const getPriceRanges = (
  product: ProductDetailsBaseModel,
): number[] | undefined => {
  return product.productTypeId === ProductTypes.DefaultProduct
    ? (product as DefaultProductDetailsModel).price.priceRanges
    : undefined;
};

const getPriceOptionId = (
  product: ProductDetailsBaseModel,
): number | undefined => {
  return product.productTypeId === ProductTypes.DefaultProduct
    ? (product as DefaultProductDetailsModel).price.priceOptionId
    : undefined;
};

const getVoucherDiscount = (
  product: ProductDetailsBaseModel,
): string | undefined => {
  switch (product.productTypeId) {
    case ProductTypes.CashBack:
      return (product as CashBackModel).cashBackValue;
    case ProductTypes.FreeLinkVoucher:
      return (product as FreeVoucherWithLinkModel).value;
    case ProductTypes.FreePdfVoucher:
      return (product as FreeVoucherWithPdfModel).value;
    case ProductTypes.ContactVoucher:
      return (product as ContactVoucherModel).discountValue;
    default:
      return undefined;
  }
};

const getVoucherCode = (
  product: ProductDetailsBaseModel,
): string | undefined => {
  return product.productTypeId === ProductTypes.FreeLinkVoucher
    ? (product as FreeVoucherWithLinkModel).voucherCode
    : undefined;
};

const getLink = (product: ProductDetailsBaseModel): string | undefined => {
  switch (product.productTypeId) {
    case ProductTypes.FreeLinkVoucher:
      // eslint-disable-next-line no-case-declarations
      const siteUrl = (product as FreeVoucherWithLinkModel).siteUrl;
      if (siteUrl.startsWith('http') || siteUrl.startsWith('https')) {
        return siteUrl;
      }

      return `https://${siteUrl}`;

    case ProductTypes.FreePdfVoucher:
      return (product as FreeVoucherWithPdfModel).pdfLink;

    default:
      return undefined;
  }
};

const getProductTaxRate = (
  product: ProductDetailsBaseModel,
): number | undefined => {
  return product.productTypeId === ProductTypes.DefaultProduct
    ? (product as DefaultProductDetailsModel).tax.employeeTaxRate
    : undefined;
};

const getTaxInformation = (
  product: ProductDetailsBaseModel,
): TaxInformation | undefined => {
  if (product.productTypeId === ProductTypes.DefaultProduct) {
    return (product as DefaultProductDetailsModel).taxInformation;
  }
  return undefined;
};

//#endregion Product props mapper

export const getBreadcrumbs = (
  path: PathModel[],
  home: Breadcrumb,
  productId?: number,
  productName?: string,
): Breadcrumb[] => {
  const breadcrumbs = [home];

  if (path.length === 0) {
    return breadcrumbs;
  }

  const categoryBasePath = TopLevelCategoryBasePath[path[0].categoryId];

  breadcrumbs.push({
    element: <span>{_.unescape(path[0].categoryName)}</span>,
    path: `${categoryBasePath}`,
  });

  breadcrumbs.push(
    ..._.drop(
      path.map((p: PathModel): Breadcrumb => {
        return {
          element: <span>{_.unescape(p.categoryName)}</span>,
          path: `${categoryBasePath}/${p.categoryId}`,
        };
      }),
      1,
    ),
  );

  if (!_.isUndefined(productId) && !_.isUndefined(productName)) {
    breadcrumbs.push({
      element: <span>{_.unescape(productName)}</span>,
      path: `/products/${productId}`,
    });
  }

  return breadcrumbs;
};
